import React, { useState } from 'react';
import { Box, Button, Typography, CircularProgress, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import config from '../config';
import { UTILITY_COLORS } from '../theme';
import { useCopilot } from '../contexts/CopilotContext';

interface GoogleAuthButtonProps {
  onAuthSuccess?: (message: string) => void;
  onAuthError?: (error: string) => void;
  className?: string;
  connectedEmail?: string | null;
  // 'secondary' renders a standard outlined button instead of the branded one (used when another primary button is already on screen)
  variant?: 'brand' | 'secondary';
  label?: string;
}

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({
  onAuthSuccess,
  onAuthError,
  connectedEmail = null,
  variant = 'brand',
  label = 'Connect Google Account',
}) => {
  const { showMessage } = useCopilot();
  const [isLoading, setIsLoading] = useState(false);
  const { palette } = useTheme();

  const isAuthenticated = connectedEmail !== null;

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Please log in first');

      const response = await fetch(`${config.apiUrl}/api/auth/google/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ redirect_uri: `${window.location.origin}/auth/google/callback` }),
      });

      if (!response.ok) throw new Error(`Failed to start Google authentication: ${response.status}`);

      const data = await response.json();
      if (data.success && data.auth_url) {
        const authTab = window.open(data.auth_url, '_blank');
        if (!authTab) throw new Error('Failed to open authentication tab. Please allow popups for this site.');

        const checkAuthInterval = setInterval(async () => {
          if (authTab.closed) {
            clearInterval(checkAuthInterval);
            try {
              const watchResponse = await fetch(`${config.apiUrl}/api/gmail/setup-watch`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
              });
              if (watchResponse.ok) console.log('✅ Gmail watch setup complete');
            } catch {}
            window.dispatchEvent(new CustomEvent('googleAuthSuccess'));
            onAuthSuccess?.('Google Account connected successfully.');
          }
        }, 500);

        setTimeout(() => clearInterval(checkAuthInterval), 300000);
      } else {
        throw new Error(data.error || 'Failed to get authorization URL');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      showMessage({ text: errorMessage });
      onAuthError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const doRevoke = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Please log in first');
      const response = await fetch(`${config.apiUrl}/api/auth/google/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        window.dispatchEvent(new CustomEvent('googleAuthSuccess'));
        onAuthSuccess?.('Google Account disconnected successfully');
      } else {
        throw new Error('Failed to revoke authentication');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to disconnect';
      showMessage({ text: errorMessage });
      onAuthError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeAuth = () => {
    showMessage({
      text: 'Disconnect Google? This will delete all existing reminders and Google Calendar events.',
      confirm: {
        label: 'Yes, disconnect',
        onConfirm: doRevoke,
      },
    });
  };

  // ── Connected state ─────────────────────────────────────────────────────────
  if (isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: palette.tokens.success }} />
          <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>{connectedEmail}</Typography>
        </Box>
        <Box
          component="button"
          onClick={handleRevokeAuth}
          disabled={isLoading}
          sx={{
            border: '1px solid #fecaca',
            backgroundColor: 'transparent',
            borderRadius: '8px',
            px: 2, py: 0.75,
            fontSize: '13px', fontWeight: 500, color: palette.tokens.error,
            cursor: 'pointer',
            transition: 'all 0.15s',
            '&:hover': { backgroundColor: UTILITY_COLORS.errorSoft },
            '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
          }}
        >
          {isLoading ? 'Disconnecting…' : 'Disconnect'}
        </Box>
      </Box>
    );
  }

  // ── Not connected state ─────────────────────────────────────────────────────
  if (variant === 'secondary') {
    return (
      <Button
        variant="outlined"
        size="small"
        onClick={handleGoogleAuth}
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={14} /> : undefined}
        sx={{ textTransform: 'none', fontWeight: 500 }}
      >
        {isLoading ? 'Connecting…' : label}
      </Button>
    );
  }

  return (
    <Box
      component="button"
      onClick={handleGoogleAuth}
      disabled={isLoading}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        border: 'none',
        borderRadius: '10px',
        px: 2.5, py: 1.25,
        background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)',
        color: '#fff',
        fontSize: '14px', fontWeight: 600,
        cursor: isLoading ? 'not-allowed' : 'pointer',
        opacity: isLoading ? 0.7 : 1,
        transition: 'all 0.15s',
        boxShadow: '0 2px 8px rgba(66,133,244,0.35)',
        '&:hover:not(:disabled)': {
          background: 'linear-gradient(135deg, #1557b0 0%, #2b6cb0 100%)',
          boxShadow: '0 4px 12px rgba(66,133,244,0.45)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {isLoading ? (
        <CircularProgress size={16} sx={{ color: '#fff' }} />
      ) : (
        <Box
          sx={{
            width: 20, height: 20, borderRadius: '4px',
            backgroundColor: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        </Box>
      )}
      {isLoading ? 'Connecting…' : label}
    </Box>
  );
};

export default GoogleAuthButton;
